import React, { useState } from 'react';

import { Button } from './Button';
import { Icon, Minus, Plus } from './Icons';
import styles from './Quantity.module.css';

type QuantityProps = {
  label?: string;
  value?: number;
  disabled?: boolean;
  onChange?: (value: number) => void;
  onQuantityBlur?: (value: number) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>;

export const Quantity: React.FC<QuantityProps> = ({
  label,
  value,
  onChange,
  onQuantityBlur,
  disabled,
  ...props
}) => {
  const [internalValue, setInternalValue] = useState<number>(value ?? 1);

  const min = 1;
  const max = 9999;

  const handleChange = (newValue: number) => {
    const clamped = Math.max(min, Math.min(max, newValue));
    setInternalValue(clamped);
    onChange?.(clamped);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      handleChange(internalValue + 1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      handleChange(internalValue - 1);
    }
  };

  return (
    <div
      className={styles.wrapper}
      onKeyDown={onKeyDown}
      role="group"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      aria-labelledby={label ? 'quantity-label' : undefined}
      onBlur={() => onQuantityBlur?.(internalValue)}
    >
      {label && (
        <span id="quantity-label" className={styles.label}>
          {label}
        </span>
      )}
      <Button
        size="xs"
        tabIndex={-1}
        aria-label="Decrease quantity"
        onClick={() => handleChange(internalValue - 1)}
        disabled={internalValue <= min || disabled}
        className={styles.button}
        icon={<Icon icon={<Minus />} />}
      />
      <input
        type="number"
        tabIndex={-1}
        value={internalValue}
        min={1}
        onKeyDown={onKeyDown}
        onChange={(e) => handleChange(Number(e.target.value))}
        style={{
          width: 40,
          textAlign: 'center',
          border: 'none',
          outline: 'none',
          fontSize: 16,
          background: 'transparent',
          appearance: 'textfield',
        }}
        aria-label={label ? undefined : 'Quantity'}
        disabled={disabled}
        {...props}
      />
      <Button
        size="xs"
        tabIndex={-1}
        aria-label="Increase quantity"
        onClick={() => handleChange(internalValue + 1)}
        disabled={internalValue >= max || disabled}
        className={styles.button}
        icon={<Icon icon={<Plus />} />}
      />
    </div>
  );
};
